import React, { useCallback, useRef } from 'react';

interface RotationControlProps {
  value: number;
  onChange: (deg: number) => void;
}

// Break-away magnetic snapping: while dragging the slider, rotation locks onto
// 0/90/180/270/360 until the user drags far enough past a snap target — then it
// releases ("breaks away") into fine 1° control until the next drag (pointer release
// re-arms the snap). Mirrors the direct-manipulation feel of Canva/Photoshop rotate
// handles without blocking exact off-axis angles (e.g. 88°).
const SNAP_TARGETS = [0, 90, 180, 270, 360];
const SNAP_THRESHOLD = 8;

function nearestSnapTarget(raw: number): { nearest: number; dist: number } {
  let nearest = SNAP_TARGETS[0];
  let minDist = Math.abs(raw - nearest);
  for (const target of SNAP_TARGETS) {
    const dist = Math.abs(raw - target);
    if (dist < minDist) {
      minDist = dist;
      nearest = target;
    }
  }
  return { nearest, dist: minDist };
}

export const RotationControl: React.FC<RotationControlProps> = ({ value, onChange }) => {
  // Break-away state: the target we most recently broke away from (drifted outside its
  // SNAP zone after having been snapped to it). While this is set, returning to that same
  // target's zone does NOT re-snap — it rests at the raw value (e.g. 88° near 90°). This
  // must persist until we either commit to a *different* target or the pointer is released;
  // it must NOT clear just because we're currently outside the zone (we always are, right
  // after breaking away), or the break-away would be forgotten instantly.
  const brokenTargetRef = useRef<number | null>(null);
  // The raw value from the previous onChange tick, used to detect the "just left a zone"
  // transition (prev tick was inside nearest's zone, this tick isn't).
  const prevRawRef = useRef<number | null>(null);

  const handleSliderInput = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      const raw = Number(e.target.value);
      const { nearest, dist } = nearestSnapTarget(raw);

      let out: number;
      if (dist <= SNAP_THRESHOLD) {
        if (brokenTargetRef.current === nearest) {
          // Returning to the target we broke away from — stay fine (allows 88°).
          out = raw;
        } else {
          // First-time approach (or approaching a different target) — snap and commit.
          out = nearest;
          brokenTargetRef.current = null;
        }
      } else {
        out = raw;
        // If the previous tick was inside nearest's zone, we just broke away from it.
        if (
          prevRawRef.current !== null &&
          Math.abs(prevRawRef.current - nearest) <= SNAP_THRESHOLD
        ) {
          brokenTargetRef.current = nearest;
        } else if (
          brokenTargetRef.current !== null &&
          brokenTargetRef.current !== nearest
        ) {
          // Moved into a different target's vicinity — forget the old break.
          brokenTargetRef.current = null;
        }
      }

      prevRawRef.current = raw;
      onChange(out);
    },
    [onChange]
  );

  const handleSliderRelease = useCallback(() => {
    brokenTargetRef.current = null;
    prevRawRef.current = null;
  }, []);

  const handleNumberInput = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      const raw = Number(e.target.value);
      if (Number.isNaN(raw)) return;
      const clamped = Math.min(360, Math.max(0, raw));
      brokenTargetRef.current = null;
      prevRawRef.current = null;
      onChange(clamped);
    },
    [onChange]
  );

  return (
    <div className="flex items-center gap-2">
      <input
        type="range"
        min={0}
        max={360}
        step={1}
        value={value}
        onChange={handleSliderInput}
        onPointerUp={handleSliderRelease}
        onMouseUp={handleSliderRelease}
        className="flex-1 accent-primary"
      />
      <input
        type="number"
        min={0}
        max={360}
        value={Math.round(value)}
        onChange={handleNumberInput}
        className="w-14 shrink-0 rounded-md border border-border bg-background px-1.5 py-1 text-xs text-center"
      />
      <span className="text-xs text-muted-foreground shrink-0">°</span>
    </div>
  );
};
